import React, { useState, useEffect, useCallback } from 'react';
import { Header } from './components/Header';
import { ActiveViewersChart } from './components/ActiveViewersChart';
import { StreamStatusCards } from './components/StreamStatusCards';
import { VmVitalsCard } from './components/VmVitalsCard';
import { ErrorsSection } from './components/ErrorsSection';
import { fetchStreamHealth } from './services/monitoringService';
import { fetchServiceHealth } from './services/healthService';
import { fetchServerErrors } from './services/errorsService';
import { ServiceHealthSection } from './components/ServiceHealthSection';
import { Radio, AlertTriangle, RefreshCw } from 'lucide-react';

function App() {
  const [streamData, setStreamData] = useState(null);
  const [dataHistory, setDataHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [countdown, setCountdown] = useState(5);

  const [serviceHealthData, setServiceHealthData] = useState(null);
  const [serviceHealthLoading, setServiceHealthLoading] = useState(true);
  const [serviceHealthError, setServiceHealthError] = useState(null);
  const [serviceHealthUpdated, setServiceHealthUpdated] = useState(null);

  const [errorsData, setErrorsData] = useState(null);
  const [errorsLoading, setErrorsLoading] = useState(true);
  const [errorsApiError, setErrorsApiError] = useState(null);
  const [errorOrigin, setErrorOrigin] = useState('all');
  const [errorType, setErrorType] = useState('all');
  const [sinceHours, setSinceHours] = useState(168);

  const loadErrors = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const result = await fetchServerErrors({
        origin: errorOrigin,
        error_type: errorType,
        since_hours: sinceHours,
      });
      if (result.error) {
        setErrorsApiError(result.error);
      } else {
        setErrorsApiError(null);
        setErrorsData(result.data);
      }
    } catch (e) {
      setErrorsApiError(e.message || 'Failed to load errors');
    } finally {
      setErrorsLoading(false);
    }
  }, [errorOrigin, errorType, sinceHours]);

  const loadServiceHealth = useCallback(async () => {
    try {
      const result = await fetchServiceHealth();
      if (result.error) {
        setServiceHealthError(result.error);
      } else {
        setServiceHealthError(null);
        setServiceHealthData(result.data);
        setServiceHealthUpdated(new Date());
      }
    } catch (e) {
      setServiceHealthError(e.message || 'Failed to load service health');
    } finally {
      setServiceHealthLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const result = await fetchStreamHealth();
      const timestamp = new Date();

      const timeLabel = timestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      setLastUpdated(timestamp);

      if (result.error) {
        setApiError(result.error);
      } else {
        setApiError(null);
        setStreamData(result.data);

        const viewers = result.data?.workerVitals?.activeViewers ?? 0;

        setDataHistory((prevHistory) => {
          const updated = [...prevHistory, { time: timeLabel, activeViewers: viewers }];
          return updated.slice(-30);
        });
      }
    } catch (error) {
      setApiError(error.message || 'Failed to communicate with monitoring server');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadServiceHealth();

    const intervalId = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          loadData();
          loadServiceHealth();
          return 5;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [loadData, loadServiceHealth]);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  const handleManualRefresh = () => {
    setCountdown(5);
    setIsLoading(true);
    setServiceHealthLoading(true);
    loadData();
    loadServiceHealth();
    loadErrors();
  };

  const handleOriginChange = (value) => {
    setErrorOrigin(value);
    setErrorType('all');
  };

  return (
    <div style={{ minHeight: '100vh', padding: '24px 20px', maxWidth: '1320px', margin: '0 auto' }}>

      <Header
        onRefresh={handleManualRefresh}
        countdown={countdown}
        lastUpdated={lastUpdated}
        apiError={apiError}
      />

      {apiError && (
        <div className="glass-card" style={{
          marginBottom: '24px',
          borderColor: 'rgba(239, 68, 68, 0.4)',
          background: 'rgba(30, 10, 10, 0.85)',
          padding: '20px 24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ef4444',
              flexShrink: 0
            }}>
              <AlertTriangle size={24} />
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f87171' }}>
                  API Connection Failed
                </h3>
                <button className="btn-emerald" onClick={handleManualRefresh} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', boxShadow: 'none' }}>
                  <RefreshCw size={14} /> Retry Connection
                </button>
              </div>

              <p style={{ fontSize: '0.88rem', color: '#fca5a5', marginTop: '6px' }}>
                {apiError}
              </p>
            </div>
          </div>
        </div>
      )}

      <ServiceHealthSection
        data={serviceHealthData}
        loading={serviceHealthLoading}
        error={serviceHealthError}
        lastUpdated={serviceHealthUpdated}
      />

      {isLoading && !streamData && !apiError ? (
        <div className="glass-card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="pulse-dot" style={{ width: '20px', height: '20px', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '1.2rem', color: '#ffffff' }}>Connecting to FarmTv Stream Metrics API...</h3>
        </div>
      ) : streamData ? (
        <>
          <ActiveViewersChart
            dataHistory={dataHistory}
            currentViewers={streamData?.workerVitals?.activeViewers ?? 0}
          />

          <StreamStatusCards
            mediaMtxVitals={streamData?.mediaMtxVitals}
            uploaderVitals={streamData?.uploaderVitals}
            workerVitals={streamData?.workerVitals}
          />

          <VmVitalsCard
            vmVitals={streamData?.vmVitals}
          />
        </>
      ) : null}

      <ErrorsSection
        data={errorsData}
        loading={errorsLoading}
        error={errorsApiError}
        origin={errorOrigin}
        errorType={errorType}
        sinceHours={sinceHours}
        onOriginChange={handleOriginChange}
        onErrorTypeChange={setErrorType}
        onSinceHoursChange={setSinceHours}
        onRefresh={loadErrors}
      />

      <footer style={{
        marginTop: '40px',
        paddingTop: '20px',
        borderTop: '1px solid rgba(16, 185, 129, 0.15)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        fontSize: '0.8rem',
        color: 'var(--text-muted)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Radio size={16} color="var(--primary-emerald)" />
          <span><strong>FarmTv Live Server Monitoring Console</strong> &copy; {new Date().getFullYear()}</span>
        </div>
      </footer>

    </div>
  );
}

export default App;
